import { 
  AnalyticsConfig, 
  AnalyticsRequest, 
  AnalyticsResponse, 
  AnalyticsDataQuery,
  AnalyticsDataPoint,
  QueryFilter,
  IAnalyticsService
} from '../types/analytics.types';
import { AnalyticsEndpointBuilder } from '../utils/endpoint-builder';

/**
 * Options for streaming analytics data
 */
export interface StreamingOptions {
  /** Enable streaming mode for large datasets */
  enabled: boolean;
  /** Format: 'json-array' (default) or 'ndjson' (newline-delimited) */
  format?: 'json-array' | 'ndjson';
  /** Callback for each data chunk received */
  onChunk?: (data: AnalyticsDataPoint[]) => void;
  /** Callback for progress updates */
  onProgress?: (received: number) => void;
  /** Callback when streaming completes */
  onComplete?: (totalRows: number) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

export class BackendAnalyticsService implements IAnalyticsService {
  private baseUrl: string;
  private appKey: string;
  private dashboardConfig?: AnalyticsConfig;
  private getAuthToken?: () => Promise<string>;
  private currentVisualizationContext?: { id: string; hasCustomQuery: boolean };
  
  // Streaming configuration
  private streamingEnabled: boolean = true; // Enable streaming by default to reduce server memory usage
  private streamingThreshold: number = 10000; // Auto-enable streaming above this row count
  
  constructor(
    baseUrl: string, 
    appKey: string,
    getAuthToken?: () => Promise<string>,
    private enableLegacyFallback: boolean = false, // Disable legacy by default
    enableStreaming: boolean = true // Enable streaming by default to prevent server OOM
  ) {
    console.log(' **************** BackendAnalyticsService constructor ******************');
    console.log('baseUrl', baseUrl);
    console.log('appKey', appKey);
    console.log('streamingEnabled', enableStreaming);
   
    this.baseUrl = baseUrl;
    this.appKey = appKey;
    this.getAuthToken = getAuthToken;
    this.streamingEnabled = enableStreaming;
  }

  /**
   * Enable or disable streaming mode globally
   */
  setStreamingEnabled(enabled: boolean): void {
    this.streamingEnabled = enabled;
  }

  /**
   * Set the row count threshold for auto-enabling streaming
   */
  setStreamingThreshold(threshold: number): void {
    this.streamingThreshold = threshold;
  }
  
  // Set current visualization context for better endpoint selection
  setVisualizationContext(context: { id: string; hasCustomQuery: boolean } | undefined): void {
    this.currentVisualizationContext = context;
  }
  
  // Set dashboard configuration for dynamic endpoint building
  setDashboardConfig(config: AnalyticsConfig): void {
    this.dashboardConfig = config;
    // Note: appKey is now set via constructor from useApiConfig hook
    // No longer need to derive it from config since dashboard.key is used
  }

  // Build authenticated headers for requests
  private async buildAuthenticatedHeaders(): Promise<Headers> {
    const headers = new Headers({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    // Add bearer token if available
    if (this.getAuthToken) {
      try {
        const token = await this.getAuthToken();
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      } catch (error) {
        console.error('🔐 [Backend Analytics] Failed to get auth token:', error);
      }
    }

    return headers;
  }

  // Configuration management
  registerConfig(config: AnalyticsConfig): void {
    // Not needed for backend service - configs are registered on the backend
  }

  getAvailableConfigs(): AnalyticsConfig[] {
    // Return empty array - configs are managed on the backend
    return [];
  }

  async getConfig(configId: string): Promise<AnalyticsConfig> {
    const headers = await this.buildAuthenticatedHeaders();
    
    // Try new API structure first, fallback to legacy
    try {
      const response = await fetch(`${this.baseUrl}/${this.appKey}/config-driven/config/${configId}`, {
        headers
      });
      if (!response.ok) throw new Error(`New API failed: ${response.statusText}`);
      return response.json();
    } catch (error) {
      console.warn(`🌐 [Backend Analytics] New config API failed, falling back to legacy:`, error);
      const response = await fetch(`${this.baseUrl}/config-driven/config/${configId}`, {
        headers
      });
      if (!response.ok) {
        throw new Error(`Failed to get config ${configId}: ${response.statusText}`);
      }
      return response.json();
    }
  }

  async getConfigs(): Promise<AnalyticsConfig[]> {
    const headers = await this.buildAuthenticatedHeaders();
    
    // Try new API structure first, fallback to legacy  
    try {
      const response = await fetch(`${this.baseUrl}/${this.appKey}/config-driven/status`, {
        headers
      });
      if (!response.ok) throw new Error(`New API failed: ${response.statusText}`);
      const status = await response.json();
      return Object.values(status.configs).map((config: any) => config.config);
    } catch (error) {
      console.warn(`🌐 [Backend Analytics] New configs API failed, falling back to legacy:`, error);
      const response = await fetch(`${this.baseUrl}/config-driven/status`, {
        headers
      });
      if (!response.ok) {
        throw new Error(`Failed to get configs: ${response.statusText}`);
      }
      const status = await response.json();
      return Object.values(status.configs).map((config: any) => config.config);
    }
  }

  // Main analytics processing
  async processAnalytics(request: AnalyticsDataQuery): Promise<AnalyticsResponse> {
    // Try the new generated API structure first
    try {
      return await this.processAnalyticsNewAPI(request);
    } catch (error) {
      if (this.enableLegacyFallback) {
        console.warn(`🌐 [Backend Analytics] New API failed, falling back to legacy config-driven:`, error);
        return await this.processAnalyticsLegacyAPI(request);
      } else {
        console.error(`🌐 [Backend Analytics] New API failed and legacy fallback disabled:`, error);
        throw new Error(`Generated API call failed: ${error.message}. Legacy fallback disabled.`);
      }
    }
  }

  /**
   * Process analytics with streaming support for large datasets.
   * Streams data progressively to reduce memory usage and improve perceived performance.
   * 
   * @param request - The analytics query request
   * @param options - Streaming options including callbacks for progress
   * @returns Promise that resolves when streaming is complete
   * 
   * @example
   * await analyticsService.processAnalyticsStreaming(request, {
   *   enabled: true,
   *   onChunk: (data) => setResults(prev => [...prev, ...data]),
   *   onProgress: (count) => setProgress(count),
   *   onComplete: (total) => console.log(`Received ${total} rows`)
   * });
   */
  async processAnalyticsStreaming(
    request: AnalyticsDataQuery,
    options: StreamingOptions
  ): Promise<AnalyticsResponse> {
    if (!options.enabled) {
      // Fall back to regular processing
      return this.processAnalytics(request);
    }

    const endpoint = this.buildDynamicEndpoint(request, this.currentVisualizationContext);
    if (!endpoint) {
      throw new Error('Could not determine appropriate endpoint for streaming request');
    }

    // Build URL with streaming parameter
    const params = new URLSearchParams();
    params.append('stream', 'true');
    
    if (request.dateRange?.start) {
      const startStr = request.dateRange.start instanceof Date 
        ? request.dateRange.start.toISOString() 
        : String(request.dateRange.start);
      params.append('date_start', startStr);
    }
    if (request.dateRange?.end) {
      const endStr = request.dateRange.end instanceof Date 
        ? request.dateRange.end.toISOString() 
        : String(request.dateRange.end);
      params.append('date_end', endStr);
    }
    if (request.limit) params.append('limit', request.limit.toString());
    if (request.offset) params.append('offset', request.offset.toString());

    const url = `${endpoint.fullUrl}?${params.toString()}`;
    const headers = await this.buildAuthenticatedHeaders();
    
    // Request NDJSON format for easier streaming parsing
    if (options.format === 'ndjson') {
      headers.set('Accept', 'application/x-ndjson');
    }

    console.log(`🌊 [Backend Analytics] Starting streaming request: ${url}`);

    try {
      const response = await fetch(url, { method: 'GET', headers });

      if (!response.ok) {
        throw new Error(`Streaming request failed: ${response.statusText}`);
      }

      const allData: AnalyticsDataPoint[] = [];
      let rowCount = 0;

      if (options.format === 'ndjson') {
        // Parse NDJSON (newline-delimited JSON) stream
        await this.parseNDJSONStream(response, (row) => {
          allData.push(row);
          rowCount++;
          options.onProgress?.(rowCount);
          
          // Batch chunks for efficiency
          if (rowCount % 100 === 0) {
            options.onChunk?.(allData.slice(-100));
          }
        });
      } else {
        // Parse JSON array stream
        await this.parseJSONArrayStream(response, (rows) => {
          allData.push(...rows);
          rowCount += rows.length;
          options.onProgress?.(rowCount);
          options.onChunk?.(rows);
        });
      }

      options.onComplete?.(rowCount);
      console.log(`🌊 [Backend Analytics] Streaming complete: ${rowCount} rows received`);

      return {
        configId: request.configId || 'streaming-response',
        data: allData,
        metadata: {
          totalRecords: rowCount,
          processingTime: 0,
          cacheHit: false,
          dataSource: 'streaming',
          lastRefreshed: new Date()
        }
      };

    } catch (error) {
      console.error('🌊 [Backend Analytics] Streaming error:', error);
      options.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Parse NDJSON (newline-delimited JSON) stream
   */
  private async parseNDJSONStream(
    response: Response,
    onRow: (row: AnalyticsDataPoint) => void
  ): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            try {
              const row = JSON.parse(buffer.trim());
              onRow(row);
            } catch (e) {
              console.warn('Failed to parse final buffer:', buffer);
            }
          }
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim()) {
            try {
              const row = JSON.parse(line);
              onRow(row);
            } catch (e) {
              console.warn('Failed to parse line:', line);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Parse JSON array stream incrementally
   * Handles streaming JSON arrays like [row1,row2,row3,...]
   */
  private async parseJSONArrayStream(
    response: Response,
    onChunk: (rows: AnalyticsDataPoint[]) => void
  ): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let inArray = false;
    let depth = 0;
    let currentObject = '';
    let totalParsed = 0;

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Process any remaining complete objects in buffer
          if (currentObject.trim()) {
            try {
              const row = JSON.parse(currentObject);
              onChunk([row]);
              totalParsed++;
            } catch (e) {
              // Incomplete object at end - ignore
            }
          }
          break;
        }

        // Append new data to buffer (NOT to currentObject)
        const newData = decoder.decode(value, { stream: true });
        buffer += newData;
        
        const rows: AnalyticsDataPoint[] = [];
        let processedUpTo = 0;

        // Parse character by character to handle nested objects
        for (let i = 0; i < buffer.length; i++) {
          const char = buffer[i];

          if (!inArray && char === '[') {
            inArray = true;
            processedUpTo = i + 1;
            continue;
          }

          if (!inArray) {
            processedUpTo = i + 1;
            continue;
          }

          if (char === '{') {
            if (depth === 0) {
              // Starting a new object - mark position
              currentObject = '{';
            } else {
              currentObject += char;
            }
            depth++;
          } else if (char === '}') {
            depth--;
            currentObject += char;
            
            if (depth === 0 && currentObject.trim()) {
              try {
                const row = JSON.parse(currentObject);
                rows.push(row);
                totalParsed++;
              } catch (e) {
                console.warn('Failed to parse object:', currentObject.substring(0, 100));
              }
              currentObject = '';
              processedUpTo = i + 1;
            }
          } else if (depth > 0) {
            currentObject += char;
          } else {
            // Skip commas and whitespace between objects when depth is 0
            processedUpTo = i + 1;
          }
        }

        // Keep only unprocessed portion of buffer (the partial object)
        if (depth > 0) {
          // We're in the middle of an object - keep currentObject as-is
          // and clear the buffer since we've extracted currentObject
          buffer = '';
        } else {
          // All complete objects processed - clear buffer
          buffer = '';
          currentObject = '';
        }

        if (rows.length > 0) {
          onChunk(rows);
        }
      }
      
      console.log(`🌊 [JSON Parser] Total objects parsed: ${totalParsed}`);
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Check if streaming should be used based on expected data size
   * Can be called before processAnalytics to determine approach
   */
  async shouldUseStreaming(request: AnalyticsDataQuery): Promise<boolean> {
    if (!this.streamingEnabled) return false;
    
    // If limit is specified and below threshold, don't stream
    if (request.limit && request.limit < this.streamingThreshold) {
      return false;
    }
    
    // Could add a HEAD request here to check Content-Length
    // For now, use streaming for any request without a limit or with high limit
    return !request.limit || request.limit >= this.streamingThreshold;
  }

  // New generated API structure
  private async processAnalyticsNewAPI(request: AnalyticsDataQuery): Promise<AnalyticsResponse> {
    // Build endpoint dynamically based on dashboard configuration and request
    const endpoint = this.buildDynamicEndpoint(request, this.currentVisualizationContext);

    if (!endpoint) {
      throw new Error('Could not determine appropriate endpoint for request');
    }
        
    // Convert the query to URL parameters for GET request
    const params = new URLSearchParams();
    if (request.dateRange?.start) {
      const startStr = request.dateRange.start instanceof Date 
        ? request.dateRange.start.toISOString() 
        : String(request.dateRange.start);
      params.append('date_start', startStr);
    }
    if (request.dateRange?.end) {
      const endStr = request.dateRange.end instanceof Date 
        ? request.dateRange.end.toISOString() 
        : String(request.dateRange.end);
      params.append('date_end', endStr);
    }
    if (request.timeRange?.start) {
      params.append('time_start', request.timeRange.start);
    }
    if (request.timeRange?.end) {
      params.append('time_end', request.timeRange.end);
    }
    if (request.limit) params.append('limit', request.limit.toString());
    if (request.offset) params.append('offset', request.offset.toString());
    
    // Enable streaming for large dataset requests to prevent server memory issues
    // Server will stream response instead of buffering entire payload
    if (this.streamingEnabled) {
      params.append('stream', 'true');
      console.log(`🌊 [Backend Analytics] Streaming enabled for request`);
    }
    
    const url = `${endpoint.fullUrl}?${params.toString()}`;
    
    const headers = await this.buildAuthenticatedHeaders();
    
    // Also set streaming header for redundancy (server checks both query param and header)
    if (this.streamingEnabled) {
      headers.set('X-Stream-Response', 'true');
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    console.log('New API response:', response);
    
    if (!response.ok) {
      throw new Error(`New API request failed: ${response.statusText}`);
    }
    
    let data: AnalyticsDataPoint[];
    let metadata: any = {};
    
    // When streaming is enabled, parse the streaming response
    if (this.streamingEnabled) {
      console.log(`🌊 [Backend Analytics] Parsing streaming response...`);
      data = [];
      let chunkCount = 0;
      await this.parseJSONArrayStream(response, (rows) => {
        chunkCount++;
        data.push(...rows);
        // Log progress every 10 chunks
        if (chunkCount % 10 === 0) {
          console.log(`🌊 [Backend Analytics] Chunk ${chunkCount}: received ${rows.length} rows, total so far: ${data.length}`);
        }
      });
      console.log(`🌊 [Backend Analytics] Streaming complete: ${data.length} rows received in ${chunkCount} chunks`);
      
      // Log sample of unique locations to verify data integrity and check for mock data contamination
      if (data.length > 0) {
        const uniqueLocations = new Set(data.map(row => (row as any).location || (row as any).location_name || (row as any).LOCATION_NAME));
        console.log(`🌊 [Backend Analytics] Unique locations in data: ${uniqueLocations.size}`);
        
        // Check for mock data location names (these should NOT be in live data)
        const mockLocationNames = ['Boston Downtown', 'Manhattan Central', 'Austin Downtown', 'Chicago Loop', 'Seattle Pike'];
        const foundMockLocations = mockLocationNames.filter(mock => uniqueLocations.has(mock));
        if (foundMockLocations.length > 0) {
          console.warn(`⚠️ [Backend Analytics] WARNING: Found mock location names in data: ${foundMockLocations.join(', ')}`);
        } else {
          console.log(`✅ [Backend Analytics] No mock location names found in data (clean live data)`);
        }
        
        // Show first 5 location names for verification
        const locationSample = Array.from(uniqueLocations).slice(0, 5);
        console.log(`🌊 [Backend Analytics] Sample locations: ${locationSample.join(', ')}`);
      }
    } else {
      // Standard JSON response parsing
      const result = await response.json();
      console.log('New API result:', result);
      
      // Handle both structured response { data: [], metadata: {} } and raw array []
      const isRawArray = Array.isArray(result);
      data = isRawArray ? result : (result.data || []);
      metadata = isRawArray ? {} : (result.metadata || {});
    }
    
    // Transform the new API response format to match expected AnalyticsResponse
    return {
      configId: request.configId || 'showcase-dashboard',
      data: data,
      metadata: {
        totalRecords: metadata.totalRecords || data.length || 0,
        processingTime: metadata.processingTime || metadata.processingTimeMs || 0,
        cacheHit: metadata.cacheHit || false,
        dataSource: this.streamingEnabled ? 'streaming' : (metadata.dataSource || 'server'),
        lastRefreshed: new Date(metadata.lastRefreshed || new Date())
      }
    };
  }

  // Legacy API structure (fallback)
  private async processAnalyticsLegacyAPI(request: AnalyticsDataQuery): Promise<AnalyticsResponse> {
    const headers = await this.buildAuthenticatedHeaders();
    
    const response = await fetch(`${this.baseUrl}/config-driven/process`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request)
    });
    
    
    if (!response.ok) {
      console.error(`🌐 [Backend Analytics] Legacy API Request failed: ${response.status} ${response.statusText}`);
      throw new Error(`Backend analytics request failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    
    return result;
  }

  // Tab-specific processing
  async processTabAnalytics(configId: string, tabId: string, request: AnalyticsDataQuery): Promise<AnalyticsResponse> {
    
    const response = await fetch(`${this.baseUrl}/config-driven/tab/${configId}/${tabId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request)
    });
    
    if (!response.ok) {
      throw new Error(`Backend tab analytics request failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    
    return result;
  }

  // Filter options
  async getFilterOptions(filterId: string, dependencies?: Record<string, any>): Promise<any[]> {
    
    // For now, return empty array - filter options would be handled by specific endpoints
    return [];
  }

  // Hierarchy operations
  async expandHierarchyNode(configId: string, nodeId: string): Promise<any[]> {
    
    // For now, return empty array - hierarchy operations would be handled by specific endpoints
    return [];
  }

  async collapseHierarchyNode(configId: string, nodeId: string): Promise<void> {
    
    // For now, no-op - hierarchy operations would be handled by specific endpoints
  }

  // Specific analytics methods
  async getKpiData(configId: string, metricId: string, filters?: QueryFilter[]): Promise<AnalyticsDataPoint[]> {
    const request: AnalyticsDataQuery = {
      configId,
      metrics: [metricId],
      filters: filters || []
    };

    const response = await this.processAnalytics(request);
    return response.data;
  }

  async getChartData(
    configId: string, 
    metrics: string[], 
    groupBy: string[], 
    filters?: QueryFilter[]
  ): Promise<AnalyticsDataPoint[]> {
    const request: AnalyticsDataQuery = {
      configId,
      metrics,
      groupBy,
      filters: filters || []
    };

    const response = await this.processAnalytics(request);
    return response.data;
  }

  async getComparativeData(
    configId: string,
    currentPeriod: { start: string; end: string },
    previousPeriod: { start: string; end: string },
    groupBy?: string[]
  ): Promise<{
    current: AnalyticsDataPoint[];
    previous: AnalyticsDataPoint[];
    comparison: AnalyticsDataPoint[];
  }> {
    
    // For now, return empty arrays - comparative data would be handled by specific endpoints
    return { current: [], previous: [], comparison: [] };
  }

  async getRealtimeData(configId: string): Promise<AnalyticsDataPoint[]> {
    
    // For now, return empty array - realtime data would be handled by specific endpoints
    return [];
  }

  async getQuickStats(date?: string): Promise<any> {
    
    // For now, return empty object - quick stats would be handled by specific endpoints
    return {};
  }

  async getTrends(
    metric: string,
    dateRange: { start: string; end: string },
    interval: string = 'day'
  ): Promise<AnalyticsDataPoint[]> {
    
    // For now, return empty array - trends would be handled by specific endpoints
    return [];
  }

  // Legacy compatibility methods
  async getBrandSummary(brandId: string): Promise<any> {
    
    // For now, return empty object - brand summary would be handled by specific endpoints
    return {};
  }

  async getBrandAnalytics(brandId: string): Promise<any> {
    
    // For now, return empty object - brand analytics would be handled by specific endpoints
    return {};
  }

  // Health check
  async healthCheck(): Promise<{ status: string; details: any }> {
    
    try {
      const response = await fetch(`${this.baseUrl}/config-driven/status`);
      
      if (!response.ok) {
        return {
          status: 'unhealthy',
          details: { error: response.statusText }
        };
      }
      
      const status = await response.json();
      
      return {
        status: 'healthy',
        details: {
          registeredConfigs: status.registeredConfigs,
          systemHealth: status.systemHealth
        }
      };
    } catch (error) {
      return {
        status: 'unhealthy',
        details: { error: (error as Error).message }
      };
    }
  }

  // Build dynamic endpoint based on request and dashboard configuration
  private buildDynamicEndpoint(request: AnalyticsDataQuery, visualizationContext?: { id: string; hasCustomQuery: boolean }): {
    type: 'visualization' | 'dataset';
    fullUrl: string;
    path: string;
  } | null {

    
    
    if (!this.dashboardConfig) {
      // Fallback to hardcoded endpoint for testing
      console.warn('🌐 [Backend Analytics] No dashboard config available, using fallback endpoint');
      /*return {
        type: 'visualization',
        fullUrl: `${this.baseUrl}/${this.appKey}/analytics/showcase-dashboard/visualizations/hierarchical-table/data`,
        path: '/analytics/showcase-dashboard/visualizations/hierarchical-table/data'
      };
      */
      return null;
    }

    // Extract available endpoints from dashboard configuration
    const endpoints = AnalyticsEndpointBuilder.extractEndpointsFromConfig(this.dashboardConfig);
     
    // Priority 1: If specific visualization context provided and it has custom query, use visualization endpoint
    const vizContext = visualizationContext || this.currentVisualizationContext;
    if (vizContext?.hasCustomQuery && endpoints.visualizations.length > 0) {
      const specificViz = endpoints.visualizations.find(v => v.id === vizContext.id);
      if (specificViz) {
        const fullUrl = `${this.baseUrl}/${this.appKey}${specificViz.endpoint}`;
        return {
          type: 'visualization',
          fullUrl,
          path: specificViz.endpoint
        };
      }
    }
    
    // Priority 2: Use dataset endpoints for general analytics requests
    if (endpoints.datasets.length > 0) {
      const dataset = endpoints.datasets[0]; // Use first available dataset
      const fullUrl = `${this.baseUrl}/${this.appKey}${dataset.endpoint}`;
   //  const fullUrl = 'http://localhost:5002/mlk/analytics/datasets/multi-location-restaurant-v9-dataset'
      console.log(`🔧 [Backend Analytics] Built dataset URL: ${fullUrl}`);
      return {
        type: 'dataset',
        fullUrl,
        path: dataset.endpoint
      };
    }
    
    // Priority 3: Fallback to any visualization endpoint if available
    if (endpoints.visualizations.length > 0) {
      const viz = endpoints.visualizations[0]; // Use first available visualization
      const fullUrl = `${this.baseUrl}/${this.appKey}${viz.endpoint}`;
      return {
        type: 'visualization',
        fullUrl,
        path: viz.endpoint
      };
    }

    // Final fallback: build a generic dataset endpoint
    const configId = this.dashboardConfig.id || 'default-dashboard';
    const path = AnalyticsEndpointBuilder.buildDatasetEndpoint(configId);
    return {
      type: 'dataset',
      fullUrl: `${this.baseUrl}/${this.appKey}${path}`,
      path
    };
  }

  // Helper method to format values (same as mock service)
  formatValue(value: any, format?: string, prefix?: string, suffix?: string): string {
    if (value == null) return '';
    
    let formattedValue = value;
    
    if (format === 'currency') {
      formattedValue = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(Number(value));
    } else if (format === 'percentage') {
      formattedValue = `${Number(value).toFixed(1)}%`;
    } else if (format === 'number') {
      formattedValue = new Intl.NumberFormat('en-US').format(Number(value));
    }
    
    if (prefix) formattedValue = `${prefix}${formattedValue}`;
    if (suffix) formattedValue = `${formattedValue}${suffix}`;
    
    return formattedValue;
  }
} 